import { motion, useReducedMotion } from "framer-motion";

// Hero constellation: business system nodes connected to a central database.
const NODES = [
  { id: "website", label: "Website", x: 118, y: 104 },
  { id: "crm", label: "CRM", x: 300, y: 62 },
  { id: "ai", label: "AI", x: 482, y: 104 },
  { id: "automation", label: "Automation", x: 506, y: 330 },
  { id: "dashboard", label: "Dashboard", x: 94, y: 330 },
  { id: "reports", label: "Reports", x: 300, y: 486 },
];
const CENTER = { x: 300, y: 268, label: "Database" };

export const HeroVisual = ({ px, py, className = "max-w-[560px]" }) => {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div style={{ x: px, y: py }} className={`relative mx-auto w-full ${className}`}>
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#1C7543]/25 blur-[90px]" />
      <svg
        viewBox="0 0 600 540"
        className="relative w-full"
        role="img"
        aria-label="Connected system diagram: website, CRM, AI, automation, dashboard and reports connected to a central database"
      >
        <defs>
          <linearGradient id="heroLine" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3CBE6E" />
            <stop offset="55%" stopColor="#1C7543" />
            <stop offset="100%" stopColor="#B8912A" />
          </linearGradient>
          <radialGradient id="heroCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D3B74D" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#D3B74D" stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle cx={CENTER.x} cy={CENTER.y} r="110" fill="url(#heroCore)" />

        {NODES.map((n, i) => (
          <motion.line
            key={n.id}
            x1={CENTER.x}
            y1={CENTER.y}
            x2={n.x}
            y2={n.y}
            stroke="url(#heroLine)"
            strokeWidth="1.4"
            strokeOpacity="0.7"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.1, delay: 1.5 + i * 0.14, ease: "easeInOut" }}
          />
        ))}

        {!reduceMotion &&
          NODES.slice(0, 4).map((n, i) => (
            <circle key={`pulse-${n.id}`} r="3.2" fill="#B8912A" opacity="0.9">
              <animateMotion
                dur={`${2.6 + i * 0.5}s`}
                begin={`${i * 0.7}s`}
                repeatCount="indefinite"
                path={`M ${CENTER.x} ${CENTER.y} L ${n.x} ${n.y}`}
              />
            </circle>
          ))}

        <motion.g
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformOrigin: `${CENTER.x}px ${CENTER.y}px` }}
        >
          <rect
            x={CENTER.x - 62}
            y={CENTER.y - 24}
            width="124"
            height="48"
            rx="16"
            fill="#1C7543"
            fillOpacity="0.22"
            stroke="url(#heroLine)"
            strokeWidth="1.6"
          />
          <circle cx={CENTER.x - 40} cy={CENTER.y} r="4" fill="#B8912A" />
          <text
            x={CENTER.x + 8}
            y={CENTER.y + 4.5}
            textAnchor="middle"
            fill="#fff"
            fontSize="13.5"
            fontWeight="700"
            fontFamily="inherit"
          >
            {CENTER.label}
          </text>
        </motion.g>

        {NODES.map((n, i) => (
          <motion.g
            key={n.id}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.0 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: `${n.x}px ${n.y}px` }}
          >
            <rect
              x={n.x - 56}
              y={n.y - 21}
              width="112"
              height="42"
              rx="14"
              fill="rgba(255,255,255,0.05)"
              stroke="rgba(255,255,255,0.22)"
              strokeWidth="1"
            />
            <circle cx={n.x - 38} cy={n.y} r="3" fill="url(#heroLine)" />
            <text
              x={n.x + 6}
              y={n.y + 4.5}
              textAnchor="middle"
              fill="#fff"
              fontSize="12.5"
              fontWeight="600"
              fontFamily="inherit"
            >
              {n.label}
            </text>
          </motion.g>
        ))}
      </svg>
    </motion.div>
  );
};
